import { useState } from 'react';

import { Button } from '@packages/fenrir/components/button';
import { Stack } from '@packages/fenrir/components/stack';
import { CustomChip } from '@packages/fenrir/components/status-chip/custom-chip';
import { Typography } from '@packages/fenrir/components/typography';
import { styled } from '@packages/fenrir/styles';

import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';

/* -------------------------------------------------------------------------------------------------
 * TechnicalDomainsListCell
 * -----------------------------------------------------------------------------------------------*/
const OrderedList = styled('ol')({
  listStyle: 'none',
  width: '100%',
  height: '100%',
  alignContent: 'center',
  paddingInline: 0,
  margin: 0,
});

type TechnicalDomainsListCellProps = {
  technicalDomains?: string[];
};

export const TechnicalDomainsListCell = ({
  technicalDomains,
}: TechnicalDomainsListCellProps) => {
  /**
   * Get an instance of the translations
   */
  const { t } = useSecurityJourneyTranslations();

  const [expanded, setExpanded] = useState(false);

  if (technicalDomains === undefined) return;

  const primaryTechnicalDomain = technicalDomains[0];

  const secondaryTechnicalDomains = technicalDomains.slice(1);

  const visibleSecondaryTechnicalDomains = expanded
    ? secondaryTechnicalDomains
    : secondaryTechnicalDomains.slice(0, 2);

  return (
    <OrderedList>
      {primaryTechnicalDomain && (
        <li key={primaryTechnicalDomain}>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" noWrap>
              {primaryTechnicalDomain}
            </Typography>
            <CustomChip
              variant="unassigned"
              label={t(
                'common.blueprintsTable.listCell.technicalDomainsListCell.primary',
              )}
            />
          </Stack>
        </li>
      )}
      {visibleSecondaryTechnicalDomains.map((item: string) => (
        <li key={item}>
          <Typography variant="body2" noWrap>
            {item}
          </Typography>
        </li>
      ))}
      {secondaryTechnicalDomains.length > 2 && (
        <li>
          <Button
            sx={{
              padding: 0,
              display: 'inline-block',
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded
              ? t('common.blueprintsTable.listCell.viewLess')
              : t('common.blueprintsTable.listCell.viewMore', {
                  count: secondaryTechnicalDomains.length - 2,
                })}
          </Button>
        </li>
      )}
    </OrderedList>
  );
};
